import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { State } from 'reducers';
import { Dialog } from 'ui/shared/containers';
import { AssignUserForm } from '../assignUserForm/AssignUserForm';
import { getBuildingByUrlSlug, HasUrlSlug } from 'selectors/buildingsSelectors';
import { useParams } from 'util/reactRouterHooks';
import { UserId } from 'shared/models';
import { callApi } from 'apiClient';
import { toError } from 'shared/util/toError';
import { useTranslation } from 'react-i18next';
import { assignBuildingUserSuccess } from 'actions/buildingUserActions';

export type Props = {
    visible: boolean;
    onHide(): void;
};

export const AssignUserDialog = ({ visible, onHide }: Props) => {
    const { t } = useTranslation();

    const params = useParams<HasUrlSlug>();
    const building = useSelector((state: State) => getBuildingByUrlSlug(state, params));

    const [ errorMessage, setErrorMessage ] = useState('');
    const [ isSubmitting, setSubmitting ] = useState(false);

    const dispatch = useDispatch();

    const handleAssign = useCallback((userId: UserId) => {
        if (!building) return;
        setSubmitting(true);
        setErrorMessage('');
        void (async () => {
            try {
                await callApi('POST', `/api/buildings/${building.id}/users`, { userId });
                dispatch(assignBuildingUserSuccess({ userId, buildingId: building.id }));
                onHide();
            }
            catch (e) {
                const err = toError(e);
                const errorMessage = t([ `ERROR.${err.message}`, 'SERVER_ERROR' ]);
                setErrorMessage(errorMessage);
            }
            finally {
                setSubmitting(false);
            }
        })();
    }, [ building, setSubmitting, setErrorMessage, dispatch, t ]);

    return (
        <Dialog
            id="assign-user-dialog"
            visible={ visible }
            titleKey={ `USERS.ASSIGN_DIALOG.TITLE` }
        >
            <AssignUserForm
                building={ building }
                isSubmitting={ isSubmitting }
                errorMessage={ errorMessage }
                onAssign={ handleAssign }
                onCancel={ onHide }
            />
        </Dialog>
    );
};

